import {dietFrequencies,FREQUENCY_DIET} from '../domain/dietFrequency';
import React, { forwardRef, useMemo, useState } from 'react';

const mealOrder = ['Colazione', 'Spuntino mattutino', 'Spuntino', 'Pranzo', 'Spuntino pomeridiano', 'Merenda', 'Cena', 'Dopo cena', 'Spuntino serale', 'Altro'];
const mealAliases = new Map([
  ['spuntino mattina', 'Spuntino mattutino'],
  ['spuntino pomeriggio', 'Spuntino pomeridiano']
]);
const pluralUnits = { porzione: 'porzioni', cucchiaio: 'cucchiai', cucchiaino: 'cucchiaini', tazza: 'tazze', bicchiere: 'bicchieri', pezzo: 'pezzi', fetta: 'fette', vasetto: 'vasetti', mestolo: 'mestoli', piatto: 'piatti' };

function canonicalMeal(value) {
  const meal = value?.trim() || 'Altro';
  return mealAliases.get(meal.toLocaleLowerCase('it-IT')) || meal;
}

export function parseSavedDietRows(plan) {
  const value = plan?.plan ?? plan?.planJson ?? [];
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function groupDietRows(rows) {
  const days = new Map();
  rows.filter(row => row?.food?.trim()).forEach(row => {
    const dayNumber = Math.max(1, Number(row.dayNumber) || 1);
    const meal = canonicalMeal(row.meal);
    if (!days.has(dayNumber)) days.set(dayNumber, new Map());
    const meals = days.get(dayNumber);
    if (!meals.has(meal)) meals.set(meal, []);
    meals.get(meal).push(row);
  });
  return [...days.entries()].sort(([a], [b]) => a - b).map(([dayNumber, meals]) => ({
    dayNumber,
    meals: [...meals.entries()]
      .sort(([a], [b]) => {
        const aIndex = mealOrder.indexOf(a);
        const bIndex = mealOrder.indexOf(b);
        return (aIndex < 0 ? mealOrder.length : aIndex) - (bIndex < 0 ? mealOrder.length : bIndex);
      })
      .map(([name, items]) => ({ name, items }))
  }));
}

export function quantityLabel(item) {
  const quantity = item.quantity === '' || item.quantity == null ? '' : String(item.quantity);
  const normalizedQuantity = quantity.trim().replace(',', '.');
  const isExactlyOne = /^1(?:\.0+)?$/.test(normalizedQuantity);
  const unit = quantity && !isExactlyOne && pluralUnits[item.unit] ? pluralUnits[item.unit] : item.unit;
  const label = [quantity, unit].filter(value => value !== '' && value != null).join(' ');
  const grams = Number(normalizedQuantity) * Number(item.gramsPerUnit);
  return item.foodCatalogItemId && item.gramsPerUnit > 0 && Number.isFinite(grams) && grams > 0 ? `${label} (${grams.toLocaleString('it-IT', {maximumFractionDigits: 2})} g)` : label;
}

function alternativeOptions(item) {
  if (Array.isArray(item.alternativeOptions) && item.alternativeOptions.length) return item.alternativeOptions;
  return Array.isArray(item.alternatives) ? item.alternatives.map(name => ({ name })) : [];
}

function PrintIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M7 8V4h10v4M7 17H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2"/><path d="M7 14h10v6H7z"/><path d="M17 12h.01"/></svg>;
}

function CopyIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="8" y="8" width="11" height="11" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1"/></svg>;
}

function buildEditableText({ plan, patientName, days, period, frequency }) {
  const lines = [
    plan?.name || 'Piano alimentare',
    patientName ? `Paziente: ${patientName}` : '',
    period ? `Periodo: ${period}` : '',
    '',
    'Indicazioni generali',
    'Seguire il piano secondo le indicazioni ricevute durante la visita. Per dubbi, difficoltà o sostituzioni contattare la nutrizionista.',
    ''
  ].filter((line, index) => line || index > 2);

  days.forEach(day => {
    lines.push(`Giorno ${day.dayNumber}${plan?.type === FREQUENCY_DIET ? ` · ${frequency.days.find(item=>item.dayNumber===day.dayNumber)?.frequency ?? "Da definire"} volte a settimana` : ""}`);
    day.meals.forEach(meal => {
      lines.push(`\n${meal.name}`);
      meal.items.forEach(item => {
        const amount = quantityLabel(item);
        lines.push(`- ${item.food}${amount ? `: ${amount}` : ''}`);
        const alternatives = alternativeOptions(item).filter(option => option?.name?.trim());
        if (alternatives.length) lines.push(`  Alternative: ${alternatives.map(option => `${option.name}${quantityLabel(option) ? ` (${quantityLabel(option)})` : ''}`).join('; ')}`);
      });
    });
    lines.push('');
  });

  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

export const PatientDietPlanPreview = forwardRef(function PatientDietPlanPreview({ plan, patient, onClose, onPrint }, ref) {
  const [copyState, setCopyState] = useState('idle');
  const rows = useMemo(() => parseSavedDietRows(plan), [plan]);
  const days = useMemo(() => groupDietRows(rows), [rows]);
  const frequency = useMemo(() => dietFrequencies(rows), [rows]);
  const frequencyMode = plan?.type === FREQUENCY_DIET;
  const incompleteFrequency = frequencyMode && !frequency.valid;
  const patientName = [patient?.firstName, patient?.lastName].filter(Boolean).join(' ');
  const period = [plan?.startsOn, plan?.endsOn].filter(Boolean).map(value => new Date(`${value}T00:00:00`).toLocaleDateString('it-IT')).join(' - ');
  const deliveredAt = new Date().toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
  const editableText = useMemo(() => buildEditableText({ plan, patientName, days, period, frequency }), [plan, patientName, days, period, frequency]);

  const copyForGoogleDocs = async () => {
    try {
      await navigator.clipboard.writeText(editableText);
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 2200);
    } catch {
      setCopyState('error');
    }
  };

  return <section id="diet-patient-preview" className="diet-patient-preview-shell" ref={ref} tabIndex="-1" aria-live="polite" aria-labelledby="diet-patient-preview-title">
    <div className="diet-preview-toolbar" data-print-hidden>
      <div><strong>Documento finale per il paziente</strong><span>Stile Sibilla/Raimo, pronto da stampare o salvare in PDF.</span></div>
      <div>
        <button type="button" className="clinic-button secondary" onClick={onClose}>Chiudi</button>
        <button type="button" className="clinic-button secondary" disabled={incompleteFrequency} onClick={copyForGoogleDocs}><CopyIcon/>{copyState === 'copied' ? 'Copiato per Docs' : copyState === 'error' ? 'Copia non riuscita' : 'Copia per Google Docs'}</button>
        <button type="button" className="clinic-button" disabled={incompleteFrequency} onClick={onPrint}><PrintIcon/>Stampa o salva PDF</button>
      </div>
    </div>

    {incompleteFrequency && <p role="alert">Frequenze settimanali da completare nell’editor prima di consegnare o stampare il piano. {frequency.message}</p>}
    <article className="diet-patient-preview">
      <header className="diet-preview-cover">
        <div className="diet-cover-art" aria-hidden="true"><span/><span/><span/></div>
        <div className="diet-preview-brand"><img src="/sibilla-logo.png" alt="Sibilla software nutrizionale" /></div>
        <div className="diet-preview-heading">
          <p>Percorso nutrizionale personalizzato</p>
          <h2 id="diet-patient-preview-title">{plan?.name || 'Piano alimentare'}</h2>
          <span>Organizzato per giornate, pasti e alternative equivalenti.</span>
        </div>
        <dl>
          <div><dt>Paziente</dt><dd>{patientName || '-'}</dd></div>
          <div><dt>Consegna</dt><dd>{deliveredAt}</dd></div>
          <div><dt>{frequencyMode ? "Organizzazione" : "Durata"}</dt><dd>{frequencyMode ? `${days.length} giornate tipo · ${frequency.valid ? "7 giorni a settimana" : "frequenze da completare"}` : `${days.length} ${days.length === 1 ? "giorno" : "giorni"}`}</dd></div>
          {period && <div><dt>Periodo</dt><dd>{period}</dd></div>}
          <div><dt>Metodo</dt><dd>{plan?.planningMethod === 'EnergyTarget' ? 'Obiettivo energetico' : 'Porzioni e frequenze'}</dd></div>
        </dl>
      </header>

      <section className="diet-preview-intro" aria-label="Indicazioni generali">
        <div><strong>Come usare questo piano</strong><p>Segui le indicazioni concordate in visita. Le alternative hanno la stessa funzione nel pasto e possono aiutarti quando un alimento non è disponibile o non è gradito.</p></div>
        <div><strong>Nota pratica</strong><p>Condimenti, idratazione, orari e sostituzioni particolari vanno mantenuti secondo le indicazioni ricevute dalla nutrizionista.</p></div>
      </section>

      {days.length ? <div className="diet-preview-days">
        {days.map(day => <section className="diet-preview-day" key={day.dayNumber} aria-labelledby={`diet-preview-day-${day.dayNumber}`}>
          <header><span>{String(day.dayNumber).padStart(2, '0')}</span><div><h3 id={`diet-preview-day-${day.dayNumber}`}>Giornata {day.dayNumber}</h3><p>{frequencyMode ? `${frequency.days.find(item=>item.dayNumber===day.dayNumber)?.frequency ?? "Da definire"} volte a settimana · ` : ""}{day.meals.length} {day.meals.length === 1 ? "momento alimentare" : "momenti alimentari"}</p></div></header>
          <div className="diet-preview-meals">
            {day.meals.map(meal => <section className="diet-preview-meal" key={meal.name}>
              <h4>{meal.name}</h4>
              <ul>
                {meal.items.map((item, index) => {
                  const alternatives = alternativeOptions(item).filter(option => option?.name?.trim());
                  return <li key={`${item.food}-${index}`}>
                    <div className="diet-preview-prescription"><strong>{item.food}</strong>{quantityLabel(item) && <span>{quantityLabel(item)}</span>}</div>
                    {alternatives.length > 0 && <p><em>Alternative</em>{alternatives.map(option => `${option.name}${quantityLabel(option) ? ` · ${quantityLabel(option)}` : ''}`).join('  /  ')}</p>}
                  </li>;
                })}
              </ul>
            </section>)}
          </div>
        </section>)}
      </div> : <div className="diet-preview-empty"><strong>Piano senza componenti</strong><span>Apri il piano nell’editor e aggiungi almeno un alimento prima di consegnarlo.</span></div>}

      <footer className="diet-preview-footer">
        <div><strong>Dott.ssa Lisa Raimo</strong><span>Biologa Nutrizionista - Ordine dei biologi AA_072324</span><span>lisaraimo.it</span></div>
        <p>Documento destinato al paziente. Le note cliniche riservate e i dati tecnici non necessari alla consegna restano nella cartella Sibilla.</p>
        <div><span>Data di consegna</span><span>Firma della nutrizionista</span></div>
      </footer>
    </article>
  </section>;
});

export default PatientDietPlanPreview;
